import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
} from "react";
import axios from "axios";
import moment from "moment-timezone";
import FadeIn from "react-fade-in";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";

import { FaChevronRight, FaUser } from "react-icons/fa";

import {
  GET_PEOPLE_CALENDAR_METRICS,
  GET_PERSON_CALENDAR_DATA,
  GET_PEOPLE_CALENDAR_PROFILES,
} from "../../../Routes";
import { generateURLOptions } from "../../../URLOptions";
import { UserContext } from "../../../storage/contexts/UserContext";

import sad from "../../../assets/images/moods/ic_sad.png";
import unhappy from "../../../assets/images/moods/ic_unhappy.png";
import okay from "../../../assets/images/moods/ic_okay.png";
import happy from "../../../assets/images/moods/ic_happy.png";
import elated from "../../../assets/images/moods/ic_elated.png";

const PeopleCalendar = () => {
  const { profile } = useContext(UserContext);
  const calendarComponentRef = useRef(null);
  const currentDateRef = useRef(
    moment.tz(profile?.timezone ?? "Australia/Adelaide").toISOString(),
  );

  const [isLoading, setIsLoading] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const [people, setPeople] = useState([]);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [showPeopleSelector, setShowPeopleSelector] = useState(false);

  const [calendarData, setCalendarData] = useState([]);
  const [eventData, setEventData] = useState([]);

  useEffect(() => {
    getMetrics();
    getPeople();
  }, []);

  useEffect(() => {
    if (selectedPerson) {
      getCalendarData(currentDateRef.current);
    }
  }, [selectedPerson]);

  useEffect(() => {
    renderCalendarData();
  }, [calendarData]);

  const getMetrics = async () => {
    try {
      const response = await axios.get(GET_PEOPLE_CALENDAR_METRICS, {
        headers: generateURLOptions(),
      });

      if (response?.data?.success === false) {
        alert(response?.data?.message ?? "");
        return;
      }

      setMetrics(response?.data?.data);
      setIsLoading(false);
    } catch (error) {
      console.error("Get People Calendar Metrics Error: ", error);
      setIsLoading(false);
    }
  };

  const getPeople = async () => {
    try {
      const response = await axios.get(GET_PEOPLE_CALENDAR_PROFILES, {
        headers: generateURLOptions(),
      });

      if (response?.data?.success === false) {
        return;
      }

      setPeople(response?.data?.data ?? []);
    } catch (error) {
      console.error("Get People Error: ", error);
    }
  };

  const getCalendarData = useCallback(
    async (dateToFetch) => {
      if (!selectedPerson) return;

      setCalendarData([]);
      setEventData([]);

      try {
        const response = await axios.post(
          GET_PERSON_CALENDAR_DATA,
          {
            userId: selectedPerson?._id,
            date: dateToFetch,
          },
          {
            headers: generateURLOptions(),
          },
        );

        if (response?.data?.success === false) {
          alert(response?.data?.message ?? "");
          return;
        }

        setCalendarData(response?.data?.data ?? []);
      } catch (error) {
        console.error("Get Person Calendar Data Error: ", error);
      }
    },
    [selectedPerson],
  );

  const renderCalendarData = () => {
    if (calendarData?.length <= 0) return;

    let eData = [];
    calendarData?.map((item) => {
      const titleChange =
        item.moodType === "log"
          ? "Checkin"
          : item.moodType === "sl"
            ? "OL"
            : item.moodType === "dfl"
              ? "DFL"
              : "";

      const moodType =
        item.mood === 0
          ? sad
          : item.mood === 1
            ? unhappy
            : item.mood === 2
              ? okay
              : item.mood === 3
                ? happy
                : item.mood === 4
                  ? elated
                  : "";

      eData.push({
        id: item?._id,
        title: titleChange,
        image: moodType,
        date: moment(item?.logDate)
          .tz(profile?.timezone ?? "Australia/Adelaide")
          .toISOString(),
      });
    });

    setEventData(eData);
  };

  const renderEventContent = (eventInfo) => (
    <div className="px-2 w-full flex flex-col justify-center items-center">
      {eventInfo?.event?.title === "Checkin" ? (
        <img
          className="w-8 h-8 object-contain"
          src={eventInfo?.event?.extendedProps?.image ?? ""}
          alt="mood"
        />
      ) : (
        <p className="text-black font-medium text-base">
          {eventInfo.event.title}
        </p>
      )}
    </div>
  );

  const handleSelectPerson = (person) => {
    currentDateRef.current = moment
      .tz(profile?.timezone ?? "Australia/Adelaide")
      .toISOString();
    setSelectedPerson(person);
    setShowPeopleSelector(false);
  };

  const handleChangePerson = () => {
    setSelectedPerson(null);
    setCalendarData([]);
    setEventData([]);
    getMetrics();
  };

  const formatPercentage = (value) => {
    if (value === null || value === undefined) return "0.00%";
    return `${Number(value).toFixed(2)}%`;
  };

  // Mood item component for metrics display
  const MoodMetricItem = ({ icon, label, percentage }) => (
    <div className="flex flex-col items-center justify-center">
      <img
        src={icon}
        alt={label}
        className="w-20 h-20 md:w-24 md:h-24 rounded-2xl object-contain"
      />
      <p className="mt-2 text-base font-semibold text-black">{label}</p>
      <p className="text-base font-semibold text-mpPurple">
        {formatPercentage(percentage)}
      </p>
    </div>
  );

  // Mood legend item for calendar view
  const MoodLegendItem = ({ icon, label }) => (
    <div className="flex flex-col items-center">
      <img
        src={icon}
        alt={label}
        className="w-10 h-10 rounded-lg object-contain"
      />
      <p className="mt-1 text-sm font-semibold text-black">{label}</p>
    </div>
  );

  // Person selector dropdown
  const PersonSelector = () => (
    <div className="relative">
      <div
        onClick={() => setShowPeopleSelector(!showPeopleSelector)}
        className="flex flex-row items-center border border-gray-300 rounded-lg p-2 cursor-pointer hover:bg-gray-50"
      >
        {selectedPerson ? (
          <>
            <img
              src={selectedPerson?.profileImage}
              alt="avatar"
              className="w-12 h-12 rounded-full object-cover"
            />
            <p className="ml-3 text-base font-semibold">
              {selectedPerson?.firstname} {selectedPerson?.lastname}
            </p>
          </>
        ) : (
          <>
            <div className="w-12 h-12 rounded-full bg-mpPurple/10 flex items-center justify-center">
              <FaUser className="text-mpPurple text-xl" />
            </div>
            <p className="ml-3 text-base font-semibold">Select Person</p>
          </>
        )}
        <div className="flex-1" />
        <FaChevronRight className="text-gray-400" />
      </div>

      {showPeopleSelector && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
          {people?.length > 0 ? (
            people.map((person) => (
              <div
                key={person?._id}
                onClick={() => handleSelectPerson(person)}
                className="flex flex-row items-center p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
              >
                <img
                  src={person?.profileImage}
                  alt="avatar"
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div className="ml-3">
                  <p className="text-sm font-semibold">
                    {person?.firstname} {person?.lastname}
                  </p>
                  <p className="text-xs text-gray-500">{person?.email}</p>
                </div>
              </div>
            ))
          ) : (
            <div className="p-3 text-center text-gray-500">No people found</div>
          )}
        </div>
      )}
    </div>
  );

  // Team Overview (initial state)
  const TeamOverview = () => (
    <div className="flex flex-col">
      <h4 className="text-xl font-bold">People</h4>
      <div className="mt-3">
        <PersonSelector />
      </div>

      <h4 className="text-xl font-bold mt-6">Team Overview</h4>

      <div className="mt-8 md:mt-12">
        {/* Row 1: Sad, Unhappy */}
        <div className="flex flex-row justify-around">
          <MoodMetricItem icon={sad} label="Sad" percentage={metrics?.sad} />
          <MoodMetricItem
            icon={unhappy}
            label="Unhappy"
            percentage={metrics?.unhappy}
          />
        </div>

        {/* Row 2: Okay, Happy */}
        <div className="flex flex-row justify-around mt-6">
          <MoodMetricItem icon={okay} label="Okay" percentage={metrics?.okay} />
          <MoodMetricItem
            icon={happy}
            label="Happy"
            percentage={metrics?.happy}
          />
        </div>

        {/* Row 3: Elated */}
        <div className="flex flex-row justify-around mt-6">
          <MoodMetricItem
            icon={elated}
            label="Elated"
            percentage={metrics?.elated}
          />
        </div>
      </div>
    </div>
  );

  // Handle calendar month change
  const handleDatesSet = useCallback(
    (dateInfo) => {
      const newDate = moment(dateInfo.start).add(15, "days").toISOString();
      if (currentDateRef.current !== newDate) {
        currentDateRef.current = newDate;
        getCalendarData(newDate);
      }
    },
    [getCalendarData],
  );

  return (
    <FadeIn>
      <div className="container mx-auto">
        <div className="w-full md:w-2/3 mx-auto px-6 md:px-0">
          <h4 className="text-xl md:text-2xl font-semibold pt-12">
            People Calendar
          </h4>

          <div className="mt-6">
            {isLoading ? (
              <div className="h-[70vh] relative flex flex-col justify-center items-center z-50">
                <div
                  className="h-10 w-10 rounded-full border-4 border-mpPurple/30 border-t-mpPurple animate-spin"
                  style={{ marginTop: 12, marginBottom: 12 }}
                />
                <h6 className="text-black font-medium">Loading...</h6>
              </div>
            ) : selectedPerson ? (
              /* Calendar View - inline to prevent FullCalendar re-initialization */
              <div className="flex flex-col">
                <div className="flex flex-row items-center justify-between">
                  <h4 className="text-xl font-bold">People</h4>
                  <button
                    onClick={handleChangePerson}
                    className="text-sm font-semibold text-mpPurple hover:underline"
                  >
                    Change Person
                  </button>
                </div>

                <div className="mt-3">
                  <PersonSelector />
                </div>

                <div className="mt-6">
                  <style>
                    {`
                      .fc-daygrid-day-events {
                        display: flex !important;
                        justify-content: center !important;
                        align-items: center !important;
                        min-height: 40px !important;
                      }
                      .fc-daygrid-event-harness {
                        position: relative !important;
                        margin: 0 auto !important;
                      }
                      .fc-event {
                        background: transparent !important;
                        border: none !important;
                      }
                    `}
                  </style>
                  <FullCalendar
                    height={"60vh"}
                    ref={calendarComponentRef}
                    plugins={[dayGridPlugin, interactionPlugin, timeGridPlugin]}
                    initialView="dayGridMonth"
                    datesSet={handleDatesSet}
                    headerToolbar={{
                      left: "prev",
                      center: "title",
                      right: "next",
                    }}
                    firstDay={1}
                    events={eventData}
                    eventContent={renderEventContent}
                    dayMaxEvents={1}
                    eventDisplay="block"
                  />
                </div>

                {/* Mood Legend */}
                <div className="mt-8 mb-8">
                  <div className="flex flex-row justify-evenly">
                    <MoodLegendItem icon={sad} label="Sad" />
                    <MoodLegendItem icon={unhappy} label="Unhappy" />
                    <MoodLegendItem icon={okay} label="Okay" />
                    <MoodLegendItem icon={happy} label="Happy" />
                    <MoodLegendItem icon={elated} label="Elated" />
                  </div>
                </div>
              </div>
            ) : (
              <TeamOverview />
            )}
          </div>
        </div>
      </div>
    </FadeIn>
  );
};

export default PeopleCalendar;
